/**
 * "Only one tab rings": cross-tab agreement for the bell's chimes and desktop
 * notifications.
 *
 * Every open tab has its own live stream and receives the same
 * `notification.upsert`. Instead of a standing leader kept alive by heartbeats
 * — which breaks exactly when it matters, because browsers throttle timers of
 * background tabs to once a minute and every hidden tab then believes it is
 * alone — the tabs agree per chime:
 *
 * 1. Each tab that wants to play key K posts a claim with its rank
 *    (focused > visible > hidden) and waits a short claim window.
 * 2. The best claim for K plays (ties: smallest tab id) and posts `played K`.
 * 3. A tab that sees `played K` (also one whose throttled timer fires late,
 *    or whose stream delivered the event late) stays silent.
 * 4. A tab that closes inside its window withdraws its claim, so the others
 *    do not defer to a tab that is gone.
 * 5. Optional throttle groups ("soft chime per contact and minute") are
 *    checked against what ANY tab played, not only this one.
 *
 * Transport: BroadcastChannel, falling back to localStorage `storage` events
 * (older Safari); without either this tab simply plays.
 */

export const TAB_RANK_HIDDEN = 0;
export const TAB_RANK_VISIBLE = 1;
export const TAB_RANK_FOCUSED = 2;

/** Long enough for a BroadcastChannel/storage round trip, short enough to feel instant. */
export const CLAIM_WINDOW_MS = 150;
/** How long claims and `played` markers are remembered (late streams, throttled timers). */
export const CLAIM_MEMORY_MS = 2 * 60_000;

export const TAB_CHANNEL_NAME = 'tg-notifications';

export type TabMessage =
  | { type: 'claim'; key: string; tab: string; rank: number }
  | { type: 'played'; key: string; tab: string; group?: string | null }
  /** A claimant leaves (tab closed / unmounted) before deciding: the others must not wait for it. */
  | { type: 'withdraw'; key: string; tab: string };

/** "At most once per group and window, across all tabs" — e.g. the soft chime per contact and minute. */
export interface ClaimThrottle {
  group: string;
  windowMs: number;
}

export interface TabTransport {
  post: (message: TabMessage) => void;
  subscribe: (listener: (message: TabMessage) => void) => () => void;
  close: () => void;
}

export interface TabLeader {
  readonly tabId: string;
  /**
   * Run `action` in exactly one tab for this key (the best-ranked one that
   * asked). With `throttle`, skip it when any tab already ran something of
   * the same group within the window.
   */
  runOnce: (key: string, action: () => void, throttle?: ClaimThrottle) => void;
  destroy: () => void;
}

export interface TabLeaderOptions {
  /** `undefined` = pick BroadcastChannel / localStorage; `null` = no coordination. */
  transport?: TabTransport | null;
  rank?: () => number;
  tabId?: string;
  now?: () => number;
  channelName?: string;
}

function isTabMessage(value: unknown): value is TabMessage {
  if (!value || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  if (typeof m.key !== 'string' || typeof m.tab !== 'string') return false;
  if (m.type === 'played' || m.type === 'withdraw') return true;
  return m.type === 'claim' && typeof m.rank === 'number';
}

export function createBroadcastTransport(name: string = TAB_CHANNEL_NAME): TabTransport | null {
  if (typeof BroadcastChannel !== 'function') return null;
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(name);
  } catch {
    return null;
  }
  return {
    post: (message) => {
      try {
        channel.postMessage(message);
      } catch {
        // Channel closed during unload — nothing to coordinate any more.
      }
    },
    subscribe: (listener) => {
      const onMessage = (event: MessageEvent) => {
        if (isTabMessage(event.data)) listener(event.data);
      };
      channel.addEventListener('message', onMessage);
      return () => channel.removeEventListener('message', onMessage);
    },
    close: () => channel.close(),
  };
}

/** Fallback: every post rewrites one key; the other tabs get a `storage` event. */
export function createStorageTransport(key: string = TAB_CHANNEL_NAME): TabTransport | null {
  try {
    const probe = `${key}:probe`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
  } catch {
    return null;
  }
  let counter = 0;
  return {
    post: (message) => {
      counter += 1;
      try {
        // The nonce makes two identical messages still two changes (= two events).
        window.localStorage.setItem(key, JSON.stringify({ ...message, nonce: `${Date.now()}-${counter}` }));
      } catch {
        // Storage full or blocked: this tab just decides alone.
      }
    },
    subscribe: (listener) => {
      const onStorage = (event: StorageEvent) => {
        if (event.key !== key || !event.newValue) return;
        let data: unknown;
        try {
          data = JSON.parse(event.newValue);
        } catch {
          return;
        }
        if (isTabMessage(data)) listener(data);
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    },
    close: () => undefined,
  };
}

/** focused > visible > hidden: the tab the person is looking at rings. */
export function documentTabRank(): number {
  if (typeof document === 'undefined') return TAB_RANK_HIDDEN;
  if (document.visibilityState !== 'visible') return TAB_RANK_HIDDEN;
  try {
    return document.hasFocus() ? TAB_RANK_FOCUSED : TAB_RANK_VISIBLE;
  } catch {
    return TAB_RANK_VISIBLE;
  }
}

function randomTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

interface Claim {
  tab: string;
  rank: number;
}

export function createTabLeader(options: TabLeaderOptions = {}): TabLeader {
  const tabId = options.tabId ?? randomTabId();
  const rank = options.rank ?? documentTabRank;
  const now = options.now ?? Date.now;
  const channelName = options.channelName ?? TAB_CHANNEL_NAME;
  const transport =
    options.transport !== undefined
      ? options.transport
      : createBroadcastTransport(channelName) ?? createStorageTransport(channelName);

  const claims = new Map<string, { at: number; byTab: Map<string, number> }>();
  const played = new Map<string, { at: number; group: string | null }>();
  /** Our own claims still inside their window, by key. */
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  let destroyed = false;

  const forgetOld = () => {
    const cutoff = now() - CLAIM_MEMORY_MS;
    claims.forEach((entry, key) => {
      if (entry.at < cutoff) claims.delete(key);
    });
    played.forEach((entry, key) => {
      if (entry.at < cutoff) played.delete(key);
    });
  };

  const addClaim = (key: string, claim: Claim) => {
    const entry = claims.get(key) ?? { at: now(), byTab: new Map<string, number>() };
    entry.byTab.set(claim.tab, claim.rank);
    claims.set(key, entry);
  };

  const markPlayed = (key: string, group: string | null) => played.set(key, { at: now(), group });

  const unsubscribe = transport?.subscribe((message) => {
    if (message.type === 'played') markPlayed(message.key, message.group ?? null);
    else if (message.type === 'withdraw') claims.get(message.key)?.byTab.delete(message.tab);
    else addClaim(message.key, { tab: message.tab, rank: message.rank });
  });

  const groupPlayedWithin = (throttle: ClaimThrottle | undefined): boolean => {
    if (!throttle) return false;
    const cutoff = now() - throttle.windowMs;
    return Array.from(played.values()).some((entry) => entry.group === throttle.group && entry.at > cutoff);
  };

  const isBestClaim = (key: string): boolean => {
    const entry = claims.get(key);
    if (!entry) return true;
    let best: Claim | null = null;
    for (const [tab, r] of Array.from(entry.byTab)) {
      if (!best || r > best.rank || (r === best.rank && tab < best.tab)) best = { tab, rank: r };
    }
    return !best || best.tab === tabId;
  };

  const runOnce = (key: string, action: () => void, throttle?: ClaimThrottle) => {
    if (destroyed) return;
    if (!transport) {
      action();
      return;
    }
    forgetOld();
    if (played.has(key) || pending.has(key) || groupPlayedWithin(throttle)) return;
    const myRank = rank();
    addClaim(key, { tab: tabId, rank: myRank });
    transport.post({ type: 'claim', key, tab: tabId, rank: myRank });
    const timer = setTimeout(() => {
      pending.delete(key);
      if (destroyed || played.has(key) || groupPlayedWithin(throttle) || !isBestClaim(key)) return;
      const group = throttle?.group ?? null;
      markPlayed(key, group);
      transport.post({ type: 'played', key, tab: tabId, group });
      action();
    }, CLAIM_WINDOW_MS);
    pending.set(key, timer);
  };

  const withdrawPending = () => {
    pending.forEach((timer, key) => {
      clearTimeout(timer);
      transport?.post({ type: 'withdraw', key, tab: tabId });
    });
    pending.clear();
  };

  // A closing tab does not unmount React: withdraw here too, so the others decide without it.
  const onPageHide = () => withdrawPending();
  if (transport && typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    withdrawPending();
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
    unsubscribe?.();
    transport?.close();
  };

  return { tabId, runOnce, destroy };
}
